/**
 * Oblique terrain-camera framing (gap G20 — feature-flagged ElevationLayer).
 *
 * A true-3D Giro3D `ElevationLayer` raises the terrain to its real height, but
 * the default fly-to frames the DEM from a near-top-down angle. Vertical relief
 * (and the vertical-exaggeration control) is then invisible because the camera
 * looks straight down the Z axis the exaggeration stretches.
 *
 * This pure helper computes a camera pose that frames the DEM from an oblique
 * angle (default ~-40° pitch) so relief reads immediately on load. It is kept
 * free of any Giro3D/WebGL dependency so the geometry can be unit-tested without
 * a renderer. The Giro3D adapter only applies the result when the elevation-mesh
 * flag is ON and a terrain layer is being framed; the flat ColorLayer path and
 * non-terrain framing are untouched.
 */

/** Default oblique pitch (degrees, negative = looking down) for terrain framing. */
export const TERRAIN_OBLIQUE_PITCH_DEG = -40;

export interface Vec3 {
  readonly x: number;
  readonly y: number;
  readonly z: number;
}

export interface ObliqueCameraPose {
  /** Camera world position (EPSG:3857 metres). */
  readonly position: Vec3;
  /** Orbit target / look-at point on the DEM floor (EPSG:3857 metres). */
  readonly target: Vec3;
  /** The applied pitch in degrees (negative = downward), after clamping. */
  readonly pitchDeg: number;
}

/**
 * Compute an oblique camera pose framing a terrain centre.
 *
 * The camera is placed on a constant north-east plan bearing (matching the
 * adapter's existing diagonal framing convention) at `pitchDeg` below the
 * horizon, looking at `(centerX, centerY, floorZ)`. Distance scales with the
 * DEM diagonal so the footprint fills the view.
 *
 * Returns `null` for non-finite or degenerate inputs so callers can fall back
 * to their existing framing instead of sending the camera to NaN-space.
 *
 * @param centerX   DEM centre X (EPSG:3857 metres).
 * @param centerY   DEM centre Y (EPSG:3857 metres).
 * @param floorZ    DEM ground floor (real min elevation, EPSG:3857 metres).
 * @param diagonalM DEM footprint diagonal in metres.
 * @param pitchDeg  Desired pitch below horizon (negative). Default ~-40°.
 */
export function computeTerrainObliquePose(
  centerX: number,
  centerY: number,
  floorZ: number,
  diagonalM: number,
  pitchDeg: number = TERRAIN_OBLIQUE_PITCH_DEG,
): ObliqueCameraPose | null {
  if (
    !Number.isFinite(centerX)
    || !Number.isFinite(centerY)
    || !Number.isFinite(floorZ)
    || !Number.isFinite(diagonalM)
    || diagonalM <= 0
    || !Number.isFinite(pitchDeg)
  ) {
    return null;
  }

  // Clamp away from straight-down (gimbal lock / no horizon) and from the
  // horizon (grazing camera that never intersects the surface).
  const clampedPitch = Math.max(-85, Math.min(-10, pitchDeg));
  const pitchRad = (clampedPitch * Math.PI) / 180;

  // Straight-line camera→target distance, scaled to the footprint with a floor
  // so tiny DEMs are still inspectable.
  const distance = Math.max(diagonalM * 0.9, 1500);

  const horiz = distance * Math.cos(pitchRad); // > 0 (cos of a small-magnitude angle)
  const vert = -distance * Math.sin(pitchRad); // pitch < 0 → sin < 0 → vert > 0 (camera is above)

  // Spread the horizontal offset evenly across X and Y so the camera looks down
  // a north-east diagonal, matching `positionCameraAt`'s framing convention.
  const horizComponent = horiz / Math.SQRT2;

  const position: Vec3 = {
    x: centerX + horizComponent,
    y: centerY + horizComponent,
    z: floorZ + vert,
  };
  const target: Vec3 = { x: centerX, y: centerY, z: floorZ };

  if (
    !Number.isFinite(position.x)
    || !Number.isFinite(position.y)
    || !Number.isFinite(position.z)
    || position.z <= floorZ
  ) {
    return null;
  }

  return { position, target, pitchDeg: clampedPitch };
}
